/* eslint-disable no-unused-vars */
import { View, StyleSheet, Text } from '@react-pdf/renderer';
import React, { useState, useEffect } from 'react';
import PDFTable from '../../PDFTable/PDFTable';
import { formatter } from '../../../utils/formaters';
import RenderTemplateBody from 'PDF/TemplateEngine/renderTemplateBody';

// Formatter para CLP (sin decimales)
const numberFormatter = new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// Formatter para UF/USD (dos decimales).
const decimalFormatter = new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Moneda fija del budget → (prefijo, formatter).
// CLP: "$X.XXX" (sin decimales). UF: "UF X.XXX,XX". USD: "US$ X.XXX,XX".
const monedaDisplay = (moneda) => {
  const m = (moneda || 'CLP').toUpperCase();
  if (m === 'UF') return { prefix: 'UF ', fmt: decimalFormatter };
  if (m === 'USD') return { prefix: 'US$ ', fmt: decimalFormatter };
  return { prefix: '$', fmt: numberFormatter };
};
import { getImageApi } from '../../../api/budget';
import Image from '../../Elements/Some/Image';
import Container from '../../Elements/Some/Container';
import { API_PATH, PATH_GET_FILES } from '../../../config';
import Normal from '../../Elements/Text/Normal';
import useFiles from 'hooks/useFiles';

const styles = StyleSheet.create({
  title: {
    fontFamily: 'Helvetica-Bold',
    fontSize: '14px',
    color: '#2c3e50',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.8cm',
    paddingBottom: '0.3cm',
    borderBottom: '2px solid #548235',
  },
  description: {
    fontSize: '11px',
    color: '#34495e',
    lineHeight: 1.5,
    textAlign: 'justify',
    marginBottom: '0.8cm',
  },
  imagesContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '0.5cm',
    marginBottom: '0.8cm',
    justifyContent: 'center',
  },
  image: {
    width: '120px',
    height: '120px',
    borderRadius: '0.2cm',
    border: '1px solid #e8e8e8',
    objectFit: 'cover',
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '0.3cm',
    border: '1px solid #e8e8e8',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  tableHeader: {
    backgroundColor: '#548235',
    display: 'flex',
    flexDirection: 'row',
  },
  headerCell: {
    padding: '0.4cm 0.3cm',
    fontFamily: 'Helvetica-Bold',
    fontSize: '10px',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    textAlign: 'center',
    borderRight: '1px solid rgba(255,255,255,0.2)',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#ffffff',
  },
  tableRowAlt: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#f8f9fa',
  },
  cell: {
    padding: '0.3cm 0.2cm',
    fontSize: '10px',
    color: '#34495e',
    textAlign: 'center',
    borderRight: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellAscensor: {
    padding: '0.3cm 0.2cm',
    fontSize: '10px',
    color: '#34495e',
    textAlign: 'center',
    borderRight: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  cellLeft: {
    padding: '0.3cm 0.2cm',
    fontSize: '10px',
    color: '#34495e',
    textAlign: 'left',
    borderRight: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
  },
  summaryRow: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#e8f4fd',
    borderTop: '2px solid #548235',
  },
  summaryLabel: {
    flex: 1,
    padding: '0.4cm 0.3cm',
    fontFamily: 'Helvetica-Bold',
    fontSize: '11px',
    color: '#2c3e50',
    textAlign: 'right',
    borderRight: '1px solid #d0d0d0',
  },
  summaryValue: {
    flex: 1,
    padding: '0.4cm 0.3cm',
    fontSize: '11px',
    color: '#2c3e50',
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
  },
  totalRow: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#548235',
    borderTop: '2px solid #2c3e50',
  },
  totalLabel: {
    flex: 1,
    padding: '0.5cm 0.3cm',
    fontFamily: 'Helvetica-Bold',
    fontSize: '12px',
    color: '#ffffff',
    textAlign: 'right',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  totalValue: {
    flex: 1,
    padding: '0.5cm 0.3cm',
    fontSize: '12px',
    color: '#ffffff',
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
  },
  // Anchos de columnas
  colN: { flex: 0.5 },
  colAscensor: { flex: 1 },
  colDescripcion: { flex: 3 },
  colCantidad: { flex: 0.5 },
  colValorUnit: { flex: 1 },
  colTotal: { flex: 1 },
});
function BudgetImage(props) {
  const { imageData } = props;
  const { fileId } = imageData;
  const { getDownloadUrl } = useFiles();

  if (fileId) {
    return <Image source={getDownloadUrl(fileId)} style={styles.image} />;
  }
  return null;
}

const calcFinalDalta = (items, discount) => {
  let valNeto = 0;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    valNeto += item.quantity * item.unitValue;
  }
  const descuento = valNeto * discount;
  const iva = (valNeto - descuento) * 0.19;
  const total = valNeto - descuento + iva;
  return {
    valNeto,
    descuento,
    iva,
    total,
  };
};

// Función para formatear los ascensores
const formatElevators = (elevatorString) => {
  if (!elevatorString) return '';

  // Si viene separado por comas, los separamos y agregamos espacios
  const elevators = elevatorString.split(',').map((elev) => elev.trim());

  // Unimos con espacios y comas para mejor legibilidad
  return elevators.join(', ');
};
function BudgetRecord(props) {
  const {
    description,
    items = [],
    images = [],
    discount,
    moneda,
    tipoCambioSnapshot,
    template,
    templateColors,
  } = props;
  if (items) {
    items.sort((a, b) => {
      return a.elevator - b.elevator;
    });
  }

  // Fase 4 del plan editor-presupuestos-lowcode: si el tenant tiene un
  // template 'presupuesto' cargado, usamos su sección 'table' (items +
  // computed + footer) via TemplateEngine — permite editar labels/columns/
  // totales/condicionales desde el editor low-code sin redeploy. Si no hay
  // template (null por fetch fallido o tenant sin configuración custom), se
  // renderiza el fallback hardcoded que vive abajo.
  const templateItemsSection = template?.sections?.find(
    (s) => s && s.type === 'table' && s.config?.dataKey === 'items'
  );

  if (templateItemsSection) {
    return (
      <View>
        <Text style={styles.title}>2. Antecedentes y Fundamentos Técnicos</Text>
        <Text style={styles.description}>{description}</Text>
        {images?.length !== 0 && (
          <View style={styles.imagesContainer}>
            {images.map((image) => (
              <BudgetImage imageData={image} key={image.id} />
            ))}
          </View>
        )}
        <RenderTemplateBody
          template={{ sections: [templateItemsSection] }}
          data={{
            items,
            discount: Number(discount || 0),
            moneda: moneda || 'CLP',
            tipo_cambio_snapshot: tipoCambioSnapshot ?? null,
          }}
          colors={templateColors}
        />
      </View>
    );
  }

  // Fallback hardcoded (legacy, V1-compatible)
  const finalData = calcFinalDalta(items, discount);
  const { prefix, fmt } = monedaDisplay(moneda);
  const fmtMoney = (n) => `${prefix}${fmt.format(n || 0)}`;
  return (
    <View>
      <Text style={styles.title}>2. Antecedentes y Fundamentos Técnicos</Text>

      {/* Descripción */}
      <Text style={styles.description}>{description}</Text>

      {/* Imágenes */}
      {images?.length !== 0 && (
        <View style={styles.imagesContainer}>
          {images.map((image) => (
            <BudgetImage imageData={image} key={image.id} />
          ))}
        </View>
      )}

      {/* Tabla de Items */}
      <View style={styles.tableContainer}>
        {/* Header de la tabla */}
        <View style={styles.tableHeader}>
          <Text style={[styles.headerCell, styles.colN]}>N°</Text>
          <Text style={[styles.headerCell, styles.colAscensor]}>Ascensor</Text>
          <Text style={[styles.headerCell, styles.colDescripcion]}>Descripción</Text>
          <Text style={[styles.headerCell, styles.colCantidad]}>Cant.</Text>
          <Text style={[styles.headerCell, styles.colValorUnit]}>Val. Unit.</Text>
          <Text style={[styles.headerCell, styles.colTotal]}>Total</Text>
        </View>

        {/* Filas de items */}
        {items.map((item, index) => (
          <View style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt} key={item.id}>
            <Text style={[styles.cell, styles.colN]}>{index + 1}</Text>
            <Text style={[styles.cellAscensor, styles.colAscensor]}>
              {formatElevators(item.elevator)}
            </Text>
            <Text style={[styles.cellLeft, styles.colDescripcion]}>{item.description}</Text>
            <Text style={[styles.cell, styles.colCantidad]}>{item.quantity}</Text>
            <Text style={[styles.cell, styles.colValorUnit]}>{fmtMoney(item.unitValue)}</Text>
            <Text style={[styles.cell, styles.colTotal]}>
              {fmtMoney(item.quantity * item.unitValue)}
            </Text>
          </View>
        ))}

        {/* Resumen de totales */}
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Valor Neto:</Text>
          <Text style={styles.summaryValue}>{fmtMoney(finalData.valNeto)}</Text>
        </View>

        {discount !== 0 && (
          <>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Descuento ({discount * 100}%):</Text>
              <Text style={styles.summaryValue}>{fmtMoney(finalData.descuento)}</Text>
            </View>
          </>
        )}

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>IVA (19%):</Text>
          <Text style={styles.summaryValue}>{fmtMoney(finalData.iva)}</Text>
        </View>

        {/* Total final */}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total Final:</Text>
          <Text style={styles.totalValue}>{fmtMoney(finalData.total)}</Text>
        </View>

        {/* Si la moneda no es CLP, mostrar el tipo de cambio snapshot */}
        {moneda && moneda !== 'CLP' && tipoCambioSnapshot ? (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Tipo de cambio ({moneda} → CLP):</Text>
            <Text style={styles.summaryValue}>${numberFormatter.format(tipoCambioSnapshot)}</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

export default BudgetRecord;
